import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, File, FileImage, FileSpreadsheet, FileText, Link2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import { AppButton, AppModal, Callout, EmptyState, PanelButton, SearchInput } from "../ui";

const FILE_ICONS = {
  XLS: FileSpreadsheet, XLSX: FileSpreadsheet, CSV: FileSpreadsheet,
  JPG: FileImage, JPEG: FileImage, PNG: FileImage,
  ZIP: File, RAR: File,
};

const CATEGORY_LABELS = { ppt: "PPT", script: "Script", notes: "Notes", other: "Other" };

const formatDate = (dateStr) => (dateStr
  ? new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })
  : "");

/**
 * MapMaterialModal — pick one of the batch's materials (batch + course level)
 * and link it to a video or a syllabus day / topic.
 *
 * Props:
 *   show, batchId, onClose
 *   targetLabel      — where it is being linked (a topic name, a video title)
 *   hint             — an extra line under the target, e.g. what linking does
 *   alreadyLinkedIds — material ids already linked here (listed, not choosable)
 *   onLink           — async (materialId); a throw is shown in the dialog
 */
const MapMaterialModal = ({ show, batchId, targetLabel, hint, alreadyLinkedIds = [], onLink, onClose }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const fetchMaterials = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError("");
    setSelected(null);
    setSearch("");
    try {
      const { data } = await apiClient.get(`/materials/batch/${batchId}`, { params: { limit: 200 } });
      const all = [...(data.data?.batchMaterials || []), ...(data.data?.courseMaterials || [])];
      setMaterials(all.filter((m) => m.status === "ready"));
    } catch {
      setError("The materials could not be loaded.");
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (show) fetchMaterials();
  }, [show, fetchMaterials]);

  const handleLink = async () => {
    if (!selected) return;
    setLinking(true);
    setError("");
    try {
      await onLink(selected);
    } catch (err) {
      setError(err?.response?.data?.message || "The material could not be linked.");
    } finally {
      setLinking(false);
    }
  };

  const linkedSet = new Set(alreadyLinkedIds.map(String));
  const q = search.trim().toLowerCase();
  const filtered = materials.filter((m) =>
    [m.title, m.topic, m.category].some((v) => (v || "").toLowerCase().includes(q)));

  return (
    <AppModal
      show={show}
      onHide={linking ? undefined : onClose}
      size="lg"
      title="Link material"
      icon={<Link2 size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onClose} disabled={linking}>Cancel</AppButton>
          <AppButton onClick={handleLink} disabled={!selected || linking}>
            {linking ? "Linking…" : "Link material"}
          </AppButton>
        </div>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        {(targetLabel || hint) && (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            {targetLabel && (
              <p className="tw-m-0 tw-text-sm tw-text-content-muted">
                To <span className="tw-font-semibold tw-text-content">{targetLabel}</span>
              </p>
            )}
            {hint && <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{hint}</p>}
          </div>
        )}
        <SearchInput value={search} onChange={setSearch} placeholder="Search by title, topic or category…" />
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        {loading ? (
          <Shimmer height={58} count={4} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={materials.length === 0 ? "No materials yet" : "Nothing matches"}
            description={materials.length === 0
              ? "Upload a material first — it can be linked here once it is in the batch."
              : "Try another search."}
          />
        ) : (
          <div className="tw-flex tw-max-h-[50vh] tw-flex-col tw-gap-2 tw-overflow-y-auto">
            {filtered.map((mat) => {
              const isLinked = linkedSet.has(String(mat._id));
              const Icon = FILE_ICONS[mat.fileType] || FileText;
              const meta = [
                formatDate(mat.sessionDate || mat.createdAt),
                CATEGORY_LABELS[mat.category] || mat.category,
                mat.fileType,
                mat.level === "course" ? "Course material" : null,
              ].filter(Boolean).join(" · ");
              return (
                <PanelButton
                  key={mat._id}
                  selected={selected === mat._id}
                  disabled={isLinked}
                  onClick={() => setSelected(mat._id)}
                >
                  <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-surface-sunken tw-text-content-muted">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                    <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{mat.title}</span>
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
                      <CalendarDays size={12} aria-hidden="true" />
                      <span className="tw-truncate">{meta}</span>
                    </span>
                    {mat.topic && <span className="tw-truncate tw-text-xs tw-text-content-subtle">{mat.topic}</span>}
                  </span>
                  {isLinked ? (
                    <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-text-content-subtle">Linked</span>
                  ) : selected === mat._id && (
                    <CheckCircle2 size={18} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />
                  )}
                </PanelButton>
              );
            })}
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default MapMaterialModal;
